import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation, Link, matchPath } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Box, Drawer, List, ListItemButton,
  ListItemIcon, ListItemText, IconButton, useMediaQuery, useTheme,
  Divider, Button, Avatar, Menu, MenuItem, Tooltip, Breadcrumbs,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  FolderSpecial as PathsIcon,
  MenuBook as StoriesIcon,
  AutoAwesome as LibraryIcon,
  People as UsersIcon,
  Settings as SettingsIcon,
  ArrowBack as ArrowBackIcon,
  Logout as LogoutIcon,
  NavigateNext as NavigateNextIcon,
  Article as StoryDotIcon,
  CheckCircle as PublishedIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { AdminNavProvider, useAdminNav } from '../context/AdminNavContext';

const drawerWidth = 248;

const navItems = [
  { title: 'Dashboard', path: '/admin', icon: <DashboardIcon />, exact: true },
  { title: 'Paths', path: '/admin/paths', icon: <PathsIcon /> },
  { title: 'Stories', path: '/admin/stories', icon: <StoriesIcon /> },
  { title: 'Library', path: '/admin/library', icon: <LibraryIcon /> },
  { title: 'Users', path: '/admin/users', icon: <UsersIcon /> },
  { title: 'Settings', path: '/admin/settings', icon: <SettingsIcon /> },
];

const SECTION_LABELS = {
  paths: 'Paths',
  stories: 'Stories',
  library: 'Library',
  users: 'Users',
  settings: 'Settings',
  matrix: 'Content Matrix',
  translations: 'Production',
  edit: 'Edit',
  new: 'New',
};

/** Build a breadcrumb trail from the URL, enriched with real path/story titles. */
const useAdminBreadcrumbs = () => {
  const location = useLocation();
  const { path, stories, storyId } = useAdminNav();
  const parts = location.pathname.split('/').filter(Boolean); // e.g. ['admin','stories','abc','translations']

  const crumbs = [{ label: 'Dashboard', to: '/admin' }];
  if (parts.length < 2) return crumbs; // just /admin

  const section = parts[1];
  crumbs.push({ label: SECTION_LABELS[section] || section, to: `/admin/${section}` });

  if (section === 'paths' && path) {
    crumbs.push({ label: path.title, to: `/admin/paths/${path.path_id}` });
    if (parts[3] && SECTION_LABELS[parts[3]]) crumbs.push({ label: SECTION_LABELS[parts[3]] });
  }

  if (section === 'stories' && storyId) {
    const story = stories.find((s) => s.story_id === storyId);
    if (path) crumbs.push({ label: path.title, to: `/admin/paths/${path.path_id}` });
    if (story) crumbs.push({ label: story.title, to: `/admin/stories/${storyId}` });
    if (parts[3] && SECTION_LABELS[parts[3]]) crumbs.push({ label: SECTION_LABELS[parts[3]] });
  }

  if ((section === 'stories' || section === 'paths') && parts[2] === 'new') {
    crumbs.push({ label: 'New' });
  }

  return crumbs;
};

const AdminBreadcrumbs = () => {
  const crumbs = useAdminBreadcrumbs();
  if (crumbs.length <= 1) return null;

  return (
    <Breadcrumbs
      separator={<NavigateNextIcon fontSize="small" />}
      sx={{ mb: 2.5, '& .MuiBreadcrumbs-li': { minWidth: 0 } }}
    >
      {crumbs.map((c, i) => {
        const last = i === crumbs.length - 1;
        if (last || !c.to) {
          return (
            <Typography
              key={i}
              variant="body2"
              color="text.primary"
              sx={{ fontWeight: 700, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {c.label}
            </Typography>
          );
        }
        return (
          <Typography
            key={i}
            component={Link}
            to={c.to}
            variant="body2"
            sx={{
              color: 'text.secondary', textDecoration: 'none', maxWidth: 220,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              '&:hover': { textDecoration: 'underline', color: 'primary.main' },
            }}
          >
            {c.label}
          </Typography>
        );
      })}
    </Breadcrumbs>
  );
};

/** Contextual sidebar section — the current path's ordered stories. */
const CurrentPathNav = ({ onNavigate }) => {
  const { path, stories, storyId } = useAdminNav();
  if (!path || stories.length === 0) return null;

  return (
    <Box sx={{ mt: 1 }}>
      <Divider sx={{ mx: 2, mb: 1 }} />
      <Typography variant="overline" sx={{ px: 2.5, color: 'text.secondary', letterSpacing: 1 }}>
        Current path
      </Typography>
      <ListItemButton
        component={Link}
        to={`/admin/paths/${path.path_id}`}
        onClick={onNavigate}
        sx={{ borderRadius: 2, mx: 1, mb: 0.5 }}
      >
        <ListItemIcon sx={{ minWidth: 34, color: 'text.secondary' }}>
          <PathsIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary={path.title}
          primaryTypographyProps={{ fontWeight: 700, fontSize: 13, noWrap: true }}
        />
      </ListItemButton>

      <List dense sx={{ px: 1, maxHeight: '40vh', overflowY: 'auto' }}>
        {stories.map((s, i) => {
          const active = s.story_id === storyId;
          return (
            <ListItemButton
              key={s.story_id}
              component={Link}
              to={`/admin/stories/${s.story_id}/translations`}
              selected={active}
              onClick={onNavigate}
              sx={{
                borderRadius: 2, mb: 0.25, pl: 1.5,
                '&.Mui-selected': {
                  bgcolor: 'primary.light', color: 'common.white',
                  '&:hover': { bgcolor: 'primary.main' },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 28, color: active ? 'common.white' : 'text.secondary' }}>
                <StoryDotIcon sx={{ fontSize: 14 }} />
              </ListItemIcon>
              <ListItemText
                primary={`${i + 1}. ${s.title}`}
                primaryTypographyProps={{ fontSize: 12.5, noWrap: true, fontWeight: active ? 700 : 500 }}
              />
              {s.is_published && (
                <PublishedIcon sx={{ fontSize: 14, color: active ? 'common.white' : 'success.main', ml: 0.5 }} />
              )}
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );
};

/**
 * AdminLayout — modern admin shell with breadcrumbs and a context-aware sidebar.
 * Auth is handled by <RequireAdmin> in App.js.
 */
const AdminLayoutInner = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [anchorEl, setAnchorEl] = useState(null);

  const toggleDrawer = () => setDrawerOpen((o) => !o);
  const closeDrawerOnMobile = isMobile ? toggleDrawer : undefined;
  const handleUserMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleUserMenuClose = () => setAnchorEl(null);
  const handleLogout = () => { handleUserMenuClose(); logout(); };

  const isActive = (item) =>
    item.exact ? location.pathname === item.path : !!matchPath(`${item.path}/*`, location.pathname) || location.pathname === item.path;

  const drawerContent = (
    <Box sx={{ overflow: 'auto', py: 1.5 }}>
      <Typography variant="overline" sx={{ px: 2.5, color: 'text.secondary', letterSpacing: 1 }}>
        Content
      </Typography>
      <List sx={{ px: 1 }}>
        {navItems.map((item) => {
          const active = isActive(item);
          return (
            <ListItemButton
              key={item.title}
              component={Link}
              to={item.path}
              selected={active}
              onClick={closeDrawerOnMobile}
              sx={{
                borderRadius: 2, mb: 0.5,
                '&.Mui-selected': { bgcolor: 'primary.main', color: 'common.white',
                  '&:hover': { bgcolor: 'primary.dark' },
                  '& .MuiListItemIcon-root': { color: 'common.white' } },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: active ? 'common.white' : 'text.secondary' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.title}
                primaryTypographyProps={{ fontWeight: active ? 700 : 500, fontSize: 14 }} />
            </ListItemButton>
          );
        })}
      </List>

      {/* Contextual: stories of the path you're currently working in */}
      <CurrentPathNav onNavigate={closeDrawerOnMobile} />
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* Top bar — clean, light */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          bgcolor: 'background.paper', color: 'text.primary',
          borderBottom: '1px solid', borderColor: 'divider',
        }}
      >
        <Toolbar>
          <IconButton aria-label="toggle drawer" edge="start" onClick={toggleDrawer} sx={{ mr: 1.5 }}>
            <MenuIcon />
          </IconButton>
          <Avatar variant="rounded" sx={{ bgcolor: 'primary.main', width: 30, height: 30, mr: 1.25, fontSize: 16, fontWeight: 700 }}>
            M
          </Avatar>
          <Typography variant="h6" noWrap sx={{ flexGrow: 1, fontWeight: 700, letterSpacing: -0.2 }}>
            Magellan <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>Admin</Box>
          </Typography>

          <Tooltip title="Back to the learner app">
            <Button startIcon={<ArrowBackIcon />} variant="outlined" size="small"
              onClick={() => navigate('/')} sx={{ mr: 1.5, textTransform: 'none', borderRadius: 2 }}>
              User Site
            </Button>
          </Tooltip>
          <IconButton onClick={handleUserMenuOpen} aria-label="account" size="small">
            <Avatar sx={{ bgcolor: 'secondary.main', width: 34, height: 34 }}>
              {user?.first_name?.charAt(0) || 'A'}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleUserMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem disabled sx={{ opacity: '1 !important' }}>
              <ListItemText primary={`${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Admin'}
                secondary={user?.email} />
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Side nav */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={drawerOpen}
        onClose={toggleDrawer}
        sx={{
          width: drawerWidth, flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth, boxSizing: 'border-box',
            bgcolor: 'background.paper', borderRight: '1px solid', borderColor: 'divider',
          },
        }}
      >
        <Toolbar />
        {drawerContent}
      </Drawer>

      {/* Main */}
      <Box
        component="main"
        sx={{
          flexGrow: 1, p: { xs: 2, md: 3 }, minWidth: 0,
          width: { md: `calc(100% - ${drawerOpen ? drawerWidth : 0}px)` },
          ml: { md: drawerOpen ? 0 : `-${drawerWidth}px` },
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar />
        <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
          <AdminBreadcrumbs />
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

const AdminLayout = () => (
  <AdminNavProvider>
    <AdminLayoutInner />
  </AdminNavProvider>
);

export default AdminLayout;
